import { useMemo } from "react"
import { useFieldArray, useForm } from "react-hook-form"

import { useAssignRef } from "shared/lib/useAssignRef"

import { zodResolver } from "@hookform/resolvers/zod"
import { CreateTeamMemberInput } from "entities/team-members/types/CreateTeamMemberInput"
import { TeamRole } from "entities/team-members/types/TeamRole"
import { useTeamRoleOptions } from "entities/teams/model/useTeamRoleOptions"
import {
  CreateTeamMemberFormData,
  createMultipleTeamMembersFormSchema
} from "features/team-member-management/types/CreateTeamMemberFormData"

import { CreateTeamMemberFormViewModel } from "../types/CreateTeamMemberFormView"
import { SendMultipleTeamInvitationsFormProps } from "../ui/CreateMultipleTeamMembersForm"

const defaultInvite: CreateTeamMemberInput = {
  email: "",
  role: TeamRole.READ
}

const defaultInitialInvites: CreateTeamMemberInput[] =
  Array(6).fill(defaultInvite)

export function useCreateMultipleTeamMembersFormViewModel({
  defaultValue,
  onSubmit,
  formRef,
  currentUserEmail,
  existingTeamMemberEmails,
  ...rest
}: SendMultipleTeamInvitationsFormProps): CreateTeamMemberFormViewModel {
  const roleOptions = useTeamRoleOptions()

  // Create dynamic validation schema based on current user and existing members
  const validationSchema = useMemo(
    () =>
      createMultipleTeamMembersFormSchema(
        currentUserEmail,
        existingTeamMemberEmails
      ),
    [currentUserEmail, existingTeamMemberEmails]
  )

  const form = useForm<CreateTeamMemberFormData>({
    defaultValues: defaultValue || { teamMembers: defaultInitialInvites },
    resolver: zodResolver(validationSchema)
  })

  useAssignRef({ ref: formRef, value: form })

  const fieldArray = useFieldArray({
    control: form.control,
    name: "teamMembers"
  })

  const handleAddInviteField = () => {
    fieldArray.append(defaultInvite)
  }

  const memoizedSubmit = useMemo(
    () => form.handleSubmit(onSubmit),
    [(form.handleSubmit, onSubmit, form.formState)]
  )

  return {
    ...rest,
    form,
    fieldArray,
    roleOptions,
    onSubmit: memoizedSubmit,
    onInviteMore: handleAddInviteField
  }
}
